import { AfterViewInit, Component, Input, OnInit } from '@angular/core';
import { appear } from '../../animations/appear';
import { BehaviorSubject, filter, takeUntil } from 'rxjs';
import { ReviewSetState } from '@models/ui/case-analytics-state/review-set-state-model';
import { Router } from "@angular/router"
import { CaseAnalyticsItemBaseComponent } from '../case-analytics-item-base.component';
import { ThemeService as ChartThemeService } from 'ng2-charts';
import { LexbeThemeService } from '@services/lexbe-theme.service';
import { BarControllerChartOptions, Chart, LegendItem } from 'chart.js';
import { Legend } from 'chart.js/dist/plugins/plugin.legend';
import { ReviewSet } from '@models/ui/case-analytics-state/review-set.model';

@Component({
    selector: 'lex-case-analytics-review-sets',
    templateUrl: './case-analytics-review-sets.component.html',
    styleUrls: ['./case-analytics-review-sets.component.scss'],
    animations: [
        appear
    ],
    standalone: false
})
export class CaseAnalyticsReviewSetsComponent extends CaseAnalyticsItemBaseComponent implements OnInit, AfterViewInit {
  @Input() set reviewSetState(state: ReviewSetState) {
    this.reviewSetState$.next(state);
  }

  public ngChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          boxHeight: 8,
          generateLabels: (chart: Chart) => {
            const labels = Chart.defaults.plugins.legend.labels.generateLabels(chart);
            const i = labels.findLastIndex(label => label.text === this.highlightedLegendItem?.text);
            if (i != -1) {
              labels[i].fillStyle = this.highlightedLegendItem.fillStyle;
              labels[i].fontColor = this.chartFontCurrentColors.hover;
            }

            return labels;
          },
          filter: (legendItem: LegendItem) => {
            let docsWithStatus = [];
            if (this.docStatusConfig.NotStarted.label === legendItem.text) {
              docsWithStatus = this.chartData.datasets[this.docStatusConfig.NotStarted.datasetIndex].data;
            }
            else if (this.docStatusConfig.NotReviewed.label === legendItem.text) {
              docsWithStatus = this.chartData.datasets[this.docStatusConfig.NotReviewed.datasetIndex].data;
            }
            else if (this.docStatusConfig.Complete.label === legendItem.text) {
              docsWithStatus = this.chartData.datasets[this.docStatusConfig.Complete.datasetIndex].data;
            }

            if (docsWithStatus.filter(doc => doc !== null).length < 1) {
              return false;
            }

            return true;
          },
        },
        onHover: (event, legendItem: LegendItem, legend: Legend) => {
          event.native.target.classList.add('onLegendItem');

          this.highlightedLegendItem = legendItem;
          const activeDatasets = [];
          const dataset = this.chartData.datasets[legendItem.datasetIndex]
          for (let i = 0; i < dataset.data.length; i++) {
              activeDatasets.push({ datasetIndex: legendItem.datasetIndex, index: i });
              if (activeDatasets.length === 1) {
                const iconHoverColor = legend.chart.data.datasets[legendItem.datasetIndex].hoverBackgroundColor.toString();
                this.highlightedLegendItem.fillStyle = iconHoverColor;
                this.highlightedLegendItem.fontColor = this.chartFontCurrentColors.hover;
              }
            }

          legend.chart.setActiveElements(
            activeDatasets,
          );
          legend.chart.update();
        },
        onLeave: (event, legendItem: LegendItem, legend: Legend) => {
          event.native.target.classList.remove('pointerOnHover');
          event.native.target.classList.remove('onLegendItem');

          this.highlightedLegendItem = legendItem;
          const iconHoverColor = legend.chart.data.datasets[legendItem.datasetIndex].backgroundColor.toString();
          this.highlightedLegendItem.fillStyle = iconHoverColor;
          this.highlightedLegendItem.fontColor = this.chartFontCurrentColors.normal;
        },
        onClick: null,
      },
      tooltip: {
        enabled: false,
      }
    },
    scales: {
      x: {
        beginAtZero: true,
        ticks: {
          display: false
        },
        stacked: true,
      },
      y: {
        beginAtZero: true,
        stacked: true,
      },
    },
  }

  public ngChartPlugins = [
    {
      id: 'hideReviewSetSummary',
      beforeEvent: (chart, args) => {
        const event = args.event;
        if ((args.inChartArea === false || event.type === 'mouseout')) {
        event.native.target.classList.remove('pointerOnHover');
          this.reviewSetSummary = null;
          if (!event.native.target.classList.contains('onLegendItem'))
          chart.setActiveElements([]);
        }
    }},
    {
      id: 'chartIsReady',
      afterRender: () => {
        if (this.chartData.datasets.length > 0) {
          this.shouldShowLoader = false;
        }
      }
    }
  ];

  public chartData = {
    labels: [],
    datasets: [],
  };

  public caseReviewSetCount: number = 0;
  public displayedReviewSetCount: number = 0;
  public caseReviewDocCount: number = 0;

  public reviewSetStatus = {
    PreReview: 'pre-review',
    Reviewing: 'reviewing',
    PostReview: 'post-review'
  }

  public docStatusConfig = {
    NotStarted: {
      label: 'Not Started',
      rgb: [216, 30, 91],
      datasetIndex: null,
    },
    NotReviewed: {
      label: 'Not Reviewed',
      rgb: [36, 62, 144],
      datasetIndex: null,
    },
    Complete: {
      label: 'Complete',
      rgb: [42, 157, 143],
      datasetIndex: null
    }
  };

  public reviewSetLookup = {
    ids: [],
    names: [],
    totalDocCounts: []
  }

  public shouldShowLoader: boolean = true;

  public reviewSetSummary: ReviewSetSummary = null;
  private reviewSetState$ = new BehaviorSubject<ReviewSetState>(null);

  constructor(private router: Router,
    lexbeThemeService: LexbeThemeService,
    chartThemeService: ChartThemeService) {
    super(lexbeThemeService, chartThemeService);
  }

  ngOnInit(): void {
    this.reviewSetState$.pipe(
      filter((response) => (!!response)),
      takeUntil(this.destroy$),
    ).subscribe(response => {
      if (response.reviewSets.length < 1) {
        this.noDataInCase$.next(true);
        return;
      }

      const reviewSets = response.reviewSets;
      this.caseReviewDocCount = response.totalDocCount;
      this.caseReviewSetCount = response.caseReviewSetCount;
      this.displayedReviewSetCount = reviewSets.length;

      this.chartData.datasets = this.initializeDatasets();

      reviewSets.forEach(reviewSet => {
        this.reviewSetLookup.ids.push(reviewSet.id);
        this.reviewSetLookup.names.push(reviewSet.title);
        this.reviewSetLookup.totalDocCounts.push(reviewSet.totalDocuments);

        const docCounts = this.formatDatasetData(reviewSet);
        this.chartData.datasets[this.docStatusConfig.NotStarted.datasetIndex].data.push(docCounts.NotStarted);
        this.chartData.datasets[this.docStatusConfig.NotReviewed.datasetIndex].data.push(docCounts.NotReviewed);
        this.chartData.datasets[this.docStatusConfig.Complete.datasetIndex].data.push(docCounts.Complete);
      });
      this.chartData.labels = this.reviewSetLookup.ids;
    });
  }


  public displayReviewSetSummary(chartEvent) {
    const active = chartEvent.active;

    if (active.length === 1) {
      const reviewSetStatusIndex = active[0].datasetIndex;
      const reviewSetIndex = active[0].index;
      const docStatusCount = this.chartData.datasets[reviewSetStatusIndex].data[reviewSetIndex];

      if (docStatusCount === null) {
        this.reviewSetSummary = null;
        return;
      }

      chartEvent.event.native.target.classList.add('pointerOnHover');

      const nextReviewSetSummary = {
        id: this.chartData.labels[reviewSetIndex],
        title: this.reviewSetLookup.names[reviewSetIndex],
        status: this.chartData.datasets[reviewSetStatusIndex].label,
        docStatusCount: docStatusCount,
        docTotalCount: this.reviewSetLookup.totalDocCounts[reviewSetIndex]
      }

      if(this.reviewSetSummary?.id === nextReviewSetSummary.id) {
        if(this.reviewSetSummary?.status !== nextReviewSetSummary.status) {
          this.reviewSetSummary.status = nextReviewSetSummary.status;
          this.reviewSetSummary.docStatusCount = nextReviewSetSummary.docStatusCount;
        }
      }
      else {
        this.reviewSetSummary = nextReviewSetSummary;
      }

      const legendItem: LegendItem = {
        text: this.reviewSetSummary.status,
        datasetIndex: reviewSetStatusIndex,
        fillStyle: this.chartData.datasets[reviewSetStatusIndex].hoverBackgroundColor.toString(),
        fontColor: this.chartFontCurrentColors.hover
      }
      this.highlightedLegendItem = legendItem;

    }
    else {
      chartEvent.event.native.target.classList.remove('pointerOnHover')
      this.reviewSetSummary = null;
      this.highlightedLegendItem = null;
    }

    chartEvent.event.chart.update();
  }


  public selectReviewSet(chartEvent) {
    if (chartEvent.active.length != 1) return;
    const reviewSetIndex = chartEvent.active[0].index;
    const reviewSetId = this.chartData.labels[reviewSetIndex];
    this.router.navigate(['/', 'discovery', 'review-sets'], {
      queryParams: {
        ReviewSetId: reviewSetId
      }
    });
  }

  private initializeDatasets(): BarControllerChartOptions[] {
    const datasets : BarControllerChartOptions[] = [];
    datasets.push(this.generateDataset(this.docStatusConfig.NotStarted));
    datasets.push(this.generateDataset(this.docStatusConfig.NotReviewed));
    datasets.push(this.generateDataset(this.docStatusConfig.Complete));
    this.docStatusConfig.NotStarted.datasetIndex = 0;
    this.docStatusConfig.NotReviewed.datasetIndex = 1;
    this.docStatusConfig.Complete.datasetIndex = 2;

    return datasets;
  }

  private generateDataset(docStatusInfo) {
    const baseColor = this.getBaseBarColor(docStatusInfo.rgb);
    const hoverColor = this.getHoverBarColor(docStatusInfo.rgb);

    return {
      label: docStatusInfo.label,
      data: [],
      backgroundColor: baseColor,
      borderColor: baseColor,
      hoverBackgroundColor: hoverColor,
      barThickness: 24,
      minBarLength: 5,
      skipNull: true,
    };
  }

  private formatDatasetData(reviewSet: ReviewSet) {
    const docCounts = {
      NotStarted: null,
      NotReviewed: null,
      Complete: null,
      Total: reviewSet.totalDocuments
    };

    if (reviewSet.status === this.reviewSetStatus.PreReview || docCounts.Total === 0) {
      docCounts.NotStarted = docCounts.Total;
    }
    else if (reviewSet.status === this.reviewSetStatus.PostReview) {
      docCounts.Complete = docCounts.Total;
    }
    else {
      docCounts.NotReviewed =  reviewSet.documentsInReview + reviewSet.remainingDocuments;
      docCounts.NotReviewed = docCounts.NotReviewed > 0 ? docCounts.NotReviewed : null;
      docCounts.Complete = reviewSet.reviewedDocuments > 0 ? reviewSet.reviewedDocuments : null;
    }

    return docCounts;
  }

  private getBaseBarColor(rgb: number[]): string {
    return `rgb(${rgb[0]}, ${rgb[1]}, ${rgb[2]})`;
  }

  private getHoverBarColor(rgb: number[]): string {
    const hoverBrightness = 40;
    const hoverRgb = rgb.map(value => value + hoverBrightness <= 255 ? value + hoverBrightness : 255);
    return `rgb(${hoverRgb[0]}, ${hoverRgb[1]}, ${hoverRgb[2]})`;

  }
}

interface ReviewSetSummary {
  id: string,
  title: string,
  status: string
  docStatusCount: number,
  docTotalCount: number
}
